import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TablePagination,
} from '@mui/material';

import { makeStyles } from '@mui/styles';

const useStyles = makeStyles({
  originalCell: {
    backgroundColor: '#f0f0f0',
  },
  updatedCell: {
    backgroundColor: '#ffffff',
  },
});


export interface AmortizationEntry {
  month: number;
  interestPayment: number;
  principalPayment: number;
  extraPayment: number;
  totalPayment: number;
  remainingBalance: number;
}

export interface AmortizationScheduleProps {
  originalSchedule: AmortizationEntry[];
  updatedSchedule: AmortizationEntry[];
  onExtraPaymentChange: (month: number, amount: number) => void;
}

const AmortizationSchedule: React.FC<AmortizationScheduleProps> = ({
  originalSchedule,
  updatedSchedule,
  onExtraPaymentChange,
}) => {
  const [page, setPage] = React.useState(0);
  const rowsPerPage = 12;

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleExtraPaymentChange = (month: number, value: string) => {
    const amount = parseFloat(value) || 0;
    onExtraPaymentChange(month, amount);
  };

  const classes = useStyles();

  return (
    <TableContainer component={Paper} style={{ marginBottom: '20px' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Mes</TableCell>
            {/* Columnas del plan original */}
            <TableCell className={classes.originalCell} align="center" colSpan={3}>
              Plan Original
            </TableCell>
            {/* Columnas del plan actualizado */}
            <TableCell className={classes.updatedCell} align="center" colSpan={4}>
              Plan Actualizado
            </TableCell>
          </TableRow>
          <TableRow>
            {/* Columnas del plan original */}
            <TableCell></TableCell>
            <TableCell className={classes.originalCell}>Intereses</TableCell>
            <TableCell className={classes.originalCell}>Amortización</TableCell>
            <TableCell className={classes.originalCell}>Pago Total</TableCell>
            {/* Columnas del plan actualizado */}
            <TableCell className={classes.updatedCell} >Intereses</TableCell>
            <TableCell className={classes.updatedCell} >Amortización</TableCell>
            <TableCell className={classes.updatedCell} >Pago Adicional</TableCell>
            <TableCell className={classes.updatedCell} >Saldo Restante</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {originalSchedule
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((originalEntry, index) => {
              const actualIndex = page * rowsPerPage + index;
              const updatedEntry = updatedSchedule[actualIndex] || updatedSchedule[updatedSchedule.length - 1];

              return (
                <TableRow key={originalEntry.month}>
                  <TableCell>{originalEntry.month}</TableCell>

                  {/* Datos del plan original */}
                  <TableCell className={classes.originalCell} >{originalEntry.interestPayment.toFixed(2)} €</TableCell>
                  <TableCell className={classes.originalCell} >{originalEntry.principalPayment.toFixed(2)} €</TableCell>
                  <TableCell className={classes.originalCell} >{originalEntry.totalPayment.toFixed(2)} €</TableCell>

                  {/* Datos del plan actualizado */}
                  <TableCell className={classes.updatedCell} >{updatedEntry.interestPayment.toFixed(2)} €</TableCell>
                  <TableCell className={classes.updatedCell} >{updatedEntry.principalPayment.toFixed(2)} €</TableCell>
                  <TableCell className={classes.updatedCell} >
                    <TextField
                      type="number"
                      value={updatedEntry.extraPayment}
                      onChange={(e) => handleExtraPaymentChange(updatedEntry.month, e.target.value)}
                      inputProps={{ style: { width: '80px' } }}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell className={classes.updatedCell} >{updatedEntry.remainingBalance.toFixed(2)} €</TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={originalSchedule.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[12]}
        labelRowsPerPage=""
      />
    </TableContainer>
  );
};

export default AmortizationSchedule;
